/**
 * PhoneField — a phone input that always produces a number with its country
 * code ("+263771234567"). A country picker sits in front of the number so a
 * client in Bulawayo and a client in Leeds both end up dial-able: WhatsApp
 * outreach, auto-link and the client response pack all need that.
 *
 * value / onChange work on the full "+<dial><national>" string, so callers
 * keep a single string field. Existing values that start with "+" are split
 * back into country + national digits; a bare local number is shown under
 * the default country (Zimbabwe) until the user picks otherwise.
 */
import React, { useMemo } from 'react';
import { TextField, MenuItem, Select, InputAdornment, FormControl } from '@mui/material';

export const PHONE_COUNTRIES = [
  { iso: 'ZW', dial: '263', name: 'Zimbabwe', flag: '🇿🇼' },
  { iso: 'ZA', dial: '27', name: 'South Africa', flag: '🇿🇦' },
  { iso: 'GB', dial: '44', name: 'United Kingdom', flag: '🇬🇧' },
  { iso: 'US', dial: '1', name: 'United States / Canada', flag: '🇺🇸' },
  { iso: 'AU', dial: '61', name: 'Australia', flag: '🇦🇺' },
  { iso: 'NZ', dial: '64', name: 'New Zealand', flag: '🇳🇿' },
  { iso: 'IE', dial: '353', name: 'Ireland', flag: '🇮🇪' },
  { iso: 'BW', dial: '267', name: 'Botswana', flag: '🇧🇼' },
  { iso: 'ZM', dial: '260', name: 'Zambia', flag: '🇿🇲' },
  { iso: 'MW', dial: '265', name: 'Malawi', flag: '🇲🇼' },
  { iso: 'MZ', dial: '258', name: 'Mozambique', flag: '🇲🇿' },
  { iso: 'NA', dial: '264', name: 'Namibia', flag: '🇳🇦' },
  { iso: 'KE', dial: '254', name: 'Kenya', flag: '🇰🇪' },
  { iso: 'TZ', dial: '255', name: 'Tanzania', flag: '🇹🇿' },
  { iso: 'UG', dial: '256', name: 'Uganda', flag: '🇺🇬' },
  { iso: 'RW', dial: '250', name: 'Rwanda', flag: '🇷🇼' },
  { iso: 'NG', dial: '234', name: 'Nigeria', flag: '🇳🇬' },
  { iso: 'GH', dial: '233', name: 'Ghana', flag: '🇬🇭' },
  { iso: 'AE', dial: '971', name: 'United Arab Emirates', flag: '🇦🇪' },
  { iso: 'QA', dial: '974', name: 'Qatar', flag: '🇶🇦' },
  { iso: 'SA', dial: '966', name: 'Saudi Arabia', flag: '🇸🇦' },
  { iso: 'DE', dial: '49', name: 'Germany', flag: '🇩🇪' },
  { iso: 'NL', dial: '31', name: 'Netherlands', flag: '🇳🇱' },
  { iso: 'FR', dial: '33', name: 'France', flag: '🇫🇷' },
  { iso: 'SE', dial: '46', name: 'Sweden', flag: '🇸🇪' },
  { iso: 'NO', dial: '47', name: 'Norway', flag: '🇳🇴' },
  { iso: 'PL', dial: '48', name: 'Poland', flag: '🇵🇱' },
  { iso: 'CH', dial: '41', name: 'Switzerland', flag: '🇨🇭' },
  { iso: 'BE', dial: '32', name: 'Belgium', flag: '🇧🇪' },
  { iso: 'ES', dial: '34', name: 'Spain', flag: '🇪🇸' },
  { iso: 'IT', dial: '39', name: 'Italy', flag: '🇮🇹' },
  { iso: 'PT', dial: '351', name: 'Portugal', flag: '🇵🇹' },
  { iso: 'CY', dial: '357', name: 'Cyprus', flag: '🇨🇾' },
  { iso: 'SG', dial: '65', name: 'Singapore', flag: '🇸🇬' },
  { iso: 'IN', dial: '91', name: 'India', flag: '🇮🇳' },
  { iso: 'CN', dial: '86', name: 'China', flag: '🇨🇳' },
];

// Longest dial code first so "+1…" never swallows "+1xx" countries and
// "+26…" resolves to 263 / 264 / 265 / 267 correctly.
const BY_DIAL_LEN = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);

/** Split "+263771234567" → { iso: 'ZW', national: '771234567' }. */
export function splitPhone(value, defaultIso = 'ZW') {
  const raw = String(value || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+') || raw.startsWith('00')) {
    const d = digits.replace(/^00/, '');
    const c = BY_DIAL_LEN.find((x) => d.startsWith(x.dial));
    if (c) return { iso: c.iso, national: d.slice(c.dial.length) };
    return { iso: defaultIso, national: d };
  }
  // A UK mobile typed as 07xxxxxxxxx (11 digits) under the ZW default.
  if (/^07\d{9}$/.test(digits)) return { iso: 'GB', national: digits.slice(1) };
  return { iso: defaultIso, national: digits.replace(/^0/, '') };
}

export function joinPhone(iso, national) {
  const c = PHONE_COUNTRIES.find((x) => x.iso === iso) || PHONE_COUNTRIES[0];
  const n = String(national || '').replace(/\D/g, '').replace(/^0/, '');
  return n ? `+${c.dial}${n}` : '';
}

/** Client-side mirror of the server rule: true when the value is dial-able. */
export function isCompletePhone(value) {
  const d = String(value || '').replace(/\D/g, '');
  if (!String(value || '').startsWith('+')) return false;
  if (d.startsWith('44') && d.length !== 12) return false;
  if (d.startsWith('263') && d.length !== 12) return false;
  return d.length >= 10 && d.length <= 15;
}

export default function PhoneField({
  value, onChange, label = 'WhatsApp / phone number', defaultIso = 'ZW',
  error, helperText, disabled, required, fullWidth = true, size, variant, sx, name = 'phone', InputProps, ...rest
}) {
  const { iso, national } = useMemo(() => splitPhone(value, defaultIso), [value, defaultIso]);
  const emit = (nextIso, nextNational) => {
    if (onChange) onChange({ target: { name, value: joinPhone(nextIso, nextNational) } });
  };
  return (
    <TextField
      {...rest}
      fullWidth={fullWidth}
      name={name}
      label={label}
      value={national}
      onChange={(e) => emit(iso, e.target.value)}
      error={error}
      helperText={helperText}
      disabled={disabled}
      required={required}
      size={size}
      variant={variant}
      sx={sx}
      inputProps={{ inputMode: 'tel', autoComplete: 'tel-national' }}
      placeholder={iso === 'ZW' ? '77 123 4567' : 'number without the leading 0'}
      InputProps={{
        ...(InputProps || {}),
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 0.5 }}>
            {InputProps?.startAdornment}
            <FormControl variant="standard" sx={{ minWidth: 92 }}>
              <Select
                value={iso}
                disabled={disabled}
                disableUnderline
                onChange={(e) => emit(e.target.value, national)}
                renderValue={(v) => {
                  const c = PHONE_COUNTRIES.find((x) => x.iso === v) || PHONE_COUNTRIES[0];
                  return `${c.flag} +${c.dial}`;
                }}
                sx={{ fontSize: 14, '& .MuiSelect-select': { py: 0.25, pr: '20px !important' } }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
              >
                {PHONE_COUNTRIES.map((c) => (
                  <MenuItem key={c.iso} value={c.iso} sx={{ fontSize: 14 }}>{c.flag} {c.name} (+{c.dial})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </InputAdornment>
        ),
      }}
    />
  );
}
